import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { useAuth } from './AuthContext';
import { getDocuments, getDocumentsByQuery } from '../services/firebase';
import { Vehicle } from '../types';

interface VehicleContextValue {
  vehicles: Vehicle[];
  availableVehicles: Vehicle[];
  assignedVehicleId?: string;
  selectedVehicleId?: string;
  setSelectedVehicleId: (id?: string) => void;
  loading: boolean;
  refresh: () => Promise<void>;
  today: string;
}

const VehicleContext = createContext<VehicleContextValue | undefined>(undefined);

function getTodayDateStringIST(): string {
  const nowIST = new Date(new Date().toLocaleString('en-US', { timeZone: 'Asia/Kolkata' }));
  const yyyy = nowIST.getFullYear();
  const mm = String(nowIST.getMonth() + 1).padStart(2, '0');
  const dd = String(nowIST.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
}

export const VehicleProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { currentUser } = useAuth();
  const [vehicles, setVehicles] = useState<Vehicle[]>([]);
  const [availableVehicles, setAvailableVehicles] = useState<Vehicle[]>([]);
  const [assignedVehicleId, setAssignedVehicleId] = useState<string | undefined>(undefined);
  const [selectedVehicleId, setSelectedVehicleId] = useState<string | undefined>(undefined);
  const [loading, setLoading] = useState<boolean>(false);
  const today = useMemo(() => getTodayDateStringIST(), []);

  const load = async () => {
    setLoading(true);
    try {
      // Load vehicles
      const allVehicles = (await getDocuments('vehicles')) as Vehicle[];
      const whId = currentUser?.warehouseId;
      const whVehicles = whId ? allVehicles.filter(v => v.warehouseId === whId) : allVehicles;

      // Optionally prefer active vehicles first (do not hide inactive in case of legacy data)
      setVehicles(whVehicles);

      // Load today's route statuses to determine vehicle assignments per route/day
      const routeStatusesToday = (await getDocumentsByQuery('route_daily_status', 'date', '==', today)) as any[];
      const assignedVehicleIds = new Set<string>();
      routeStatusesToday.forEach(rs => { if (rs.vehicleId) assignedVehicleIds.add(rs.vehicleId); });

      // If salesman, detect their route and assigned vehicle from route_daily_status
      if (currentUser?.role === 'salesman') {
        const myBeats = (await getDocumentsByQuery('beats', 'forDate', '==', today)) as any[];
        const myBeat = myBeats.find(b => b.salesmanId === currentUser.id);
        const myRouteId: string | undefined = myBeat?.routeId;
        let myVehicleId: string | undefined = undefined;
        if (myRouteId) {
          const myStatus = routeStatusesToday.find((rs: any) => rs.routeId === myRouteId);
          myVehicleId = myStatus?.vehicleId as string | undefined;
        }
        setAssignedVehicleId(myVehicleId);
        if (myVehicleId) setSelectedVehicleId(prev => prev || myVehicleId);
      } else {
        setAssignedVehicleId(undefined);
      }

      // Available vehicles = in my warehouse and not assigned to any route/day today
      const baseAvailable = whVehicles.filter(v => !assignedVehicleIds.has(v.vehicleId));
      const patched = assignedVehicleId
        ? [...baseAvailable, ...whVehicles.filter(v => v.vehicleId === assignedVehicleId)]
        : baseAvailable;

      const map = new Map(patched.map(v => [v.vehicleId, v] as const));
      setAvailableVehicles(Array.from(map.values()));
    } catch (e) {
      console.error('VehicleProvider load error', e);
      setVehicles([]);
      setAvailableVehicles([]);
      setAssignedVehicleId(undefined);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (currentUser) {
      load();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser?.id, currentUser?.warehouseId, currentUser?.role, today]);

  const value: VehicleContextValue = {
    vehicles,
    availableVehicles,
    assignedVehicleId,
    selectedVehicleId,
    setSelectedVehicleId,
    loading,
    refresh: load,
    today
  };

  return (
    <VehicleContext.Provider value={value}>
      {children}
    </VehicleContext.Provider>
  );
};

export const useVehicleContext = (): VehicleContextValue => {
  const ctx = useContext(VehicleContext);
  if (!ctx) throw new Error('useVehicleContext must be used within a VehicleProvider');
  return ctx;
};
